/**
 * Reemplaza el AuthProvider original (Supabase Auth + RPC auth_policy/my_authz)
 * por uno que habla con el Worker propio. La forma publica (MyAuthz, session,
 * mfa, mfaSatisfied) se mantiene identica a proposito -- es lo que permite que
 * AppShell, guards.tsx y las paginas reales no necesiten cambios.
 *
 * MFA -- ya NO es un stub. El backend lo tiene completo (src/mfa.ts) detras
 * de un flag apagado por defecto (`app_settings['auth.require_mfa']`); esta
 * pasada conecta el flujo real: cuando el login devuelve
 * mfa_enroll_required/mfa_challenge_required (nunca pasa hoy con el flag
 * apagado, pero si se prende), no hay sesion real todavia -- solo un
 * "pending token" de 5 minutos que sirve unicamente para /mfa/enroll* y
 * /mfa/challenge (ver src/auth.ts en el backend). Se guarda una sesion
 * PARCIAL (`session` con el email, `mfaSatisfied=false`) para que
 * RequireSession no rebote a /login -- eso es lo que deja que guards.tsx,
 * sin tocar, redirija a /mfa/inscribir o /mfa/verificar.
 *
 * Simplificacion que SI sigue siendo deliberada:
 * - permissions: no hay tabla roles/permissions en D1 todavia. Se deriva un
 *   set fijo desde isInstitutional (ver toMyAuthz). Cuando el RBAC completo
 *   exista en el backend, esto vuelve a ser un fetch real, no una regla local.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EMPTY_AUTHZ, type MyAuthz } from '@rat/shared';
import { apiFetch, clearToken, getToken, login as backendLogin, setToken } from '../lib/backend';

export interface Session {
  user: { email: string };
}

type MfaState = 'unknown' | 'not_required' | 'not_enrolled' | 'needs_challenge' | 'verified';

interface MeResponse {
  userId: string;
  email: string;
  isInstitutional: boolean;
  isAuditor: boolean;
  units: Array<{ unit_id: string; name: string; unit_role: 'jefe' | 'colaborador' | 'consulta' }>;
}

function toMyAuthz(me: MeResponse): MyAuthz {
  // El institucional (DPD/superadmin) tiene el mismo alcance amplio que en el
  // sistema real: puede editar/enviar cualquier actividad ademas de revisarla
  // -- no solo aprobar/observar/cerrar. Detectado al probar el flujo real:
  // sin esto, BORRADOR no ofrecia ninguna transicion a un usuario institucional.
  //
  // isAuditor faltaba aqui (bug encontrado al conectar el tablero por rol):
  // el backend ya distingue auditor de institucional desde que se agrego el
  // rol (canReadAll() en authz.ts acepta ambos para LECTURA, nunca para
  // crear/editar/revisar) pero el frontend nunca lo leia de /me, asi que un
  // auditor caia en el set de colaborador (con activity.create, que no le
  // corresponde) y no tenia forma de llegar a los tableros.
  //
  // 'unit.read.all', no 'institutional.view': el auditor lee todo (tableros,
  // actividades de cualquier unidad) pero NO administra -- admin.ts,
  // catalog-admin.ts y audit.ts exigen isInstitutional a secas, sin
  // excepcion para auditor (ver sus propios comentarios). 'institutional.view'
  // en este frontend gatea ademas Catalogos/Usuarios/Auditoria (mismo
  // colapso documentado en router.tsx/README del backend), asi que dárselo
  // al auditor le mostraria enlaces de administracion que el backend le
  // rechazaria con 403. 'unit.read.all' ya existia en @rat/shared
  // (PERMISSIONS, pensado justamente para esto) pero nunca se usaba aqui.
  const permissions: MyAuthz['permissions'] = me.isInstitutional
    ? [
        'activity.read.all',
        'activity.create',
        'activity.update.all',
        'activity.update.own_unit',
        'activity.submit',
        'activity.review',
        'activity.close',
        'activity.reopen',
        'institutional.view',
        'audit.read',
      ]
    : me.isAuditor
      ? ['activity.read.all', 'unit.read.all']
      : ['activity.read.own_unit', 'activity.create', 'activity.update.own_unit', 'activity.submit'];
  return {
    user_id: me.userId,
    // Siempre true aqui: a este punto (hydrate() con una sesion REAL) el
    // segundo factor, si el flag lo exige, ya se completo -- nunca se llega
    // a /me con una sesion pendiente de MFA (ver AuthProvider mas abajo).
    mfa: true,
    institutional: me.isInstitutional,
    permissions,
    units: me.units.map((u) => ({
      unit_id: u.unit_id,
      code: u.unit_id,
      name: u.name,
      unit_role: u.unit_role,
      includes_descendants: false,
    })),
  };
}

interface AuthContextValue {
  loading: boolean;
  session: Session | null;
  mfa: MfaState;
  mfaSatisfied: boolean;
  /** Solo mientras mfa es 'not_enrolled'/'needs_challenge' -- lo consumen MfaEnroll.tsx/MfaChallenge.tsx. */
  pendingMfaToken: string | null;
  authz: MyAuthz;
  refreshAuthz: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /** Llamado por MfaEnroll.tsx/MfaChallenge.tsx al terminar el segundo factor -- entrega la sesion real. */
  completeMfa: (sessionToken: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [authz, setAuthz] = useState<MyAuthz>(EMPTY_AUTHZ);
  const [mfa, setMfa] = useState<MfaState>('not_required');
  const [pendingMfaToken, setPendingMfaToken] = useState<string | null>(null);

  async function hydrate(): Promise<void> {
    if (!getToken()) {
      setSession(null);
      setAuthz(EMPTY_AUTHZ);
      setLoading(false);
      return;
    }
    try {
      const me = await apiFetch<MeResponse>('/me');
      setSession({ user: { email: me.email } });
      setAuthz(toMyAuthz(me));
      setMfa('not_required'); // llegar aca via un token real ya prueba que el segundo factor (si aplicaba) esta resuelto
      setPendingMfaToken(null);
    } catch {
      clearToken();
      setSession(null);
      setAuthz(EMPTY_AUTHZ);
    }
    setLoading(false);
  }

  useEffect(() => {
    void hydrate();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      session,
      mfa,
      mfaSatisfied: mfa === 'not_required' || mfa === 'verified',
      pendingMfaToken,
      authz,
      refreshAuthz: hydrate,
      signIn: async (email: string, password: string) => {
        const outcome = await backendLogin(email, password);
        if (outcome.status === 'ok') {
          await hydrate();
          return;
        }
        // Sin sesion real todavia -- una "parcial" (solo el email) es
        // suficiente para que RequireSession no rebote a /login; guards.tsx
        // hace el resto redirigiendo a /mfa/inscribir o /mfa/verificar.
        setSession({ user: { email } });
        setPendingMfaToken(outcome.pendingToken);
        setMfa(outcome.status === 'mfa_enroll_required' ? 'not_enrolled' : 'needs_challenge');
      },
      completeMfa: async (sessionToken: string) => {
        setToken(sessionToken);
        setMfa('verified');
        setPendingMfaToken(null);
        await hydrate();
      },
      signOut: async () => {
        clearToken();
        setSession(null);
        setAuthz(EMPTY_AUTHZ);
        setMfa('not_required');
        setPendingMfaToken(null);
      },
    }),
    [loading, session, authz, mfa, pendingMfaToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de <AuthProvider>');
  return ctx;
}
